import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { get as _get } from 'lodash';
import Moment from 'moment';

import { useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Input from '@mui/material/Input';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import SensorsService from '../../../data/sensors-service';
import PolicyService from '../../../data/policy-service';
import BacktestingService from '../../../data/backtesting-service';
import DataContext from '../../../data/data-context';

import DateTimePicker from '../../../components/styled/datetimepicker';
import Validator, { checkValidity, useValidate } from '../../../components/validator';

// const useStyles = makeStyles(theme => ({
//     flexFix: {
//         display: 'flex',
//     },
//     select: {
//         width: '100%',
//     },
// }));

const toUnix = d => Math.floor(d.getTime() / 1000);
const toDate = d => {
    const dd = new Date(d * 1000);
    if (isNaN(dd)) return new Date();
    return dd.toISOString();
};

const YESTERDAY = Moment().subtract(24, 'hours').toDate();
const TODAY = Moment().toDate();

const BacktestingCreate = (props) => {
    // const classes = useStyles();
    const theme = useTheme();
    const params = useParams();
    const navigate = useNavigate();
    const { refreshBacktestingJobs } = useContext(DataContext);
    const [sensors, setSensors] = useState([]);
    const [suricataPolicies, setSuricataPolicies] = useState([]);
    const [zeekPolicies, setZeekPolicies] = useState([]);
    const [TIPolicies, setTIPolicies] = useState([]);
    const [btJob, setBTJob] = useState({
        name: '',
        description: '',
        sensor: '',
        addtoQueue: true,
        data: {
            suricata_policy: 'default',
            zeek_policy: 'default',
            ti_indicators_policy: 'default',
            generateMetadata: false,
            pcapdirs: ['/var/bricata/data/pcaps'],
            timefilter_enabled: false,
            timefilter_start: toUnix(YESTERDAY),
            timefilter_end: toUnix(TODAY),
        },
    });
    const [edit, setEdit] = useState(false);
    const jobForm = useValidate();

    useEffect(() => {
        getSensors();
        getSuricataPolicies();
        getZeekPolicies();
        getTIPolicies();

        params.jobName ? setEdit(true) : (() => {})();
    }, [params.jobName]);

    const getJob = useCallback(() => {
        BacktestingService.getJob(params.jobName).then(res => {
            setBTJob(res);
        }, err => {
            toast.error('Unable to get job for editing...');
            console.error('Unable to get job for editing ::: ', err);
        });
    }, [params.jobName]);

    const getSensors = () => {
        SensorsService.getData().then(res => {
            res = res.filter(v => (_get(v, 'health_metrics.appliance_type', '') === 'btsensor'));
            setSensors(res);
        }, err => {
            console.error(err);
        });
    };

    const getSuricataPolicies = () => {
        PolicyService.getPolicies('suricata_rules').then(res => {
            setSuricataPolicies(res.filter(d => d.source === 'cmc'));
        }, err => {
            console.error(err);
        });
    };

    const getZeekPolicies = () => {
        PolicyService.getPolicies('bro_ti').then(res => {
            setZeekPolicies(res.filter(d => d.source === 'cmc'));
        }, err => {
            console.error(err);
        });
    };

    const getTIPolicies = () => {
        PolicyService.getPolicies('zeek_indicators').then(res => {
            setTIPolicies(res.filter(d => d.source === 'cmc'));
        }, err => {
            console.error(err);
        });
    };

    const handleUpdateName = (e, validID, valid) => {
        jobForm.set({ ...jobForm.get(), [validID]: valid });

        setBTJob({
            ...btJob,
            name: e.target.value,
        });
    };

    const handleUpdateSensor = (e) => {
        setBTJob({
            ...btJob,
            sensor: e.target.value,
        });
    };

    const handleUpdateSuricataPolicy = (e) => {
        const d = { ...btJob };
        d.data.suricata_policy = e.target.value;
        setBTJob(d);
    };

    const handleUpdateZeekPolicy = (e) => {
        const d = { ...btJob };
        d.data.zeek_policy = e.target.value;
        setBTJob(d);
    };

    const handleUpdateTIPolicy = (e) => {
        const d = { ...btJob };
        d.data.ti_indicators_policy = e.target.value;
        setBTJob(d);
    };

    const handleUpdateDescription = (e) => {
        setBTJob({
            ...btJob,
            description: e.target.value,
        });
    };

    const handleUpdatePCAPDirectory = (e, i) => {
        const d = { ...btJob };
        if (!d.data.pcapdirs.length) d.data.pcapdirs.push('');
        d.data.pcapdirs[i] = e.target.value;
        setBTJob(d);
    };

    const handleAddPCAPDir = (i) => {
        const d = { ...btJob };
        d.data.pcapdirs.push('');
        setBTJob(d);
    };

    const handleRemovePCAPDir = (i) => {
        const d = { ...btJob };
        d.data.pcapdirs.splice(i, 1);
        setBTJob(d);
    };

    // const handleGenerateMetadata = () => {
    //     let d = {...btJob};
    //     d.data.generateMetadata = !d.data.generateMetadata;
    //     setBTJob(d);
    // };

    // const handleAddToQueue = () => {
    //     let d = {...btJob};
    //     d.addtoQueue = !d.addtoQueue;
    //     setBTJob(d);
    // };

    const handleToggleTimefilter = e => {
        const d = { ...btJob };
        d.data.timefilter_enabled = e.target.checked;
        setBTJob(d);
    };

    const handleUpdateTimeStart = v => {
        const d = { ...btJob };
        d.data.timefilter_start = v.unix();
        setBTJob(d);
    };

    const handleUpdateTimeEnd = v => {
        const d = { ...btJob };
        d.data.timefilter_end = v.unix();
        setBTJob(d);
    };

    const handleManageJobs = () => {
        navigate('/views/backtesting');
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const handleCreateJob = () => {
        if (edit) {
            BacktestingService.updateJob(btJob).then(() => {
                refreshBacktestingJobs();
                toast.success('Job updated successfully');
                navigate(-1);
            }, err => {
                toast.error('Unable to update job...');
                console.error(err);
            });
        } else {
            BacktestingService.createJob(btJob).then(res => {
                refreshBacktestingJobs();
                navigate('/views/backtesting');
            }, err => {
                toast.error(err);
                console.error(err);
            });
        }
    };

    const isValid = useCallback(() => {
        return (btJob.sensor || '').length > 0 &&
            (btJob.data.suricata_policy || '').length > 0 &&
            checkValidity(jobForm.get());
    }, [jobForm, btJob]);

    useEffect(() => {
        if (edit) {
            getJob();
        }
    }, [edit, getJob]);

    // memorize these guys for less rendering
    const startDate = useMemo(() => toDate(btJob.data.timefilter_start || YESTERDAY),
        [btJob.data.timefilter_start]);
    const endDate = useMemo(() => toDate(btJob.data.timefilter_end || TODAY),
        [btJob.data.timefilter_end]);

    return (
        <Box>
            <Grid container>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end">
                        <Button variant="contained" color="primary" onClick={handleManageJobs}>Manage Jobs</Button>
                    </Box>
                </Grid>
            </Grid>
            <Paper elevation={3}>
                <Box p={3} mt={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant='h6'>{edit ? 'Edit Job' : 'Create New Job'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Box mb={2}>
                                <Validator
                                    valueType="policyName"
                                    errorText="Name is invalid"
                                    fullWidth
                                    label="Job Name"
                                    value={btJob.name}
                                    onChange={handleUpdateName}
                                />
                            </Box>
                            <Box mb={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="BT Sensor"
                                    value={btJob.sensor}
                                    onChange={handleUpdateSensor}
                                    input={<Input id="sensor-select-input" />}
                                >
                                    {sensors.map((v, i) => (<MenuItem key={'sensor-select-' + i} value={v.uuid}>{v.data.name}</MenuItem>))}
                                </TextField>
                            </Box>
                            <Box mb={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Suricata Policy"
                                    value={btJob.data.suricata_policy}
                                    onChange={handleUpdateSuricataPolicy}
                                    input={<Input id="suricata-select-input" />}
                                >
                                    {suricataPolicies.map((v, i) => (<MenuItem key={'suricata-select-' + i} value={v.name}>{v.name}</MenuItem>))}
                                </TextField>
                            </Box>
                            <Box mb={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Zeek Policy"
                                    value={btJob.data.zeek_policy}
                                    onChange={handleUpdateZeekPolicy}
                                    input={<Input id="zeek-select-input" />}
                                >
                                    {zeekPolicies.map((v, i) => (<MenuItem key={'zeek-select-' + i} value={v.name}>{v.name}</MenuItem>))}
                                </TextField>
                            </Box>
                            <Box mb={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Threat Intel Indicators Policy"
                                    value={btJob.data.ti_indicators_policy}
                                    onChange={handleUpdateTIPolicy}
                                    input={<Input id="ti-select-input" />}
                                >
                                    {TIPolicies.map((v, i) => (<MenuItem key={'ti-select-' + i} value={v.name}>{v.name}</MenuItem>))}
                                </TextField>
                            </Box>
                            <Box mb={2}>
                                <Box ml={-2}>
                                    <FormControlLabel
                                        label="Enable Time Filter"
                                        labelPlacement="start"
                                        onChange={handleToggleTimefilter}
                                        checked={btJob.data.timefilter_enabled}
                                        control={<Checkbox value={btJob.data.timefilter_enabled} />}
                                    />
                                </Box>
                                <Box
                                    mt={1}
                                    display={btJob.data.timefilter_enabled ? 'flex' : 'none'}
                                    style={{ justifyContent: 'space-between' }}
                                >
                                    <Box>
                                        <DateTimePicker
                                            label="Start"
                                            ampm={false}
                                            value={startDate}
                                            onChange={handleUpdateTimeStart}
                                            inputFormat={theme.date.long}
                                            disableMaskedInput={true}
                                            showTodayButton={true}
                                            maxDate={endDate}
                                        />
                                    </Box>
                                    <Box ml={2}>
                                        <DateTimePicker
                                            label="End"
                                            ampm={false}
                                            value={endDate}
                                            onChange={handleUpdateTimeEnd}
                                            inputFormat={theme.date.long}
                                            disableMaskedInput={true}
                                            showTodayButton={true}
                                            minDate={startDate}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box mb={2}>
                                <TextField
                                    label="Job Description"
                                    fullWidth
                                    rows={4.15}
                                    multiline
                                    value={btJob.description}
                                    onChange={handleUpdateDescription}
                                />
                            </Box>
                            <Box display="flex">
                                <Box width="90%" mb={2}>
                                    {btJob.data.pcapdirs.map((v, i) => {
                                        return (
                                            <Box mt={i ? 2 : 0} key={'pcap-dir-' + i}>
                                                <TextField
                                                    fullWidth
                                                    label="PCAP Directory"
                                                    value={v}
                                                    onChange={(e) => { handleUpdatePCAPDirectory(e, i) }}
                                                    InputProps={{
                                                        endAdornment: i
                                                            ? <Tooltip title="Remove directory from list">
                                                                <IconButton color="secondary" onClick={() => { handleRemovePCAPDir(i) }}>
                                                                    <RemoveIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            : null
                                                    }}
                                                />
                                            </Box>
                                        );
                                    })}
                                    {/* <TextField
                                        label="PCAP Directory"
                                        fullWidth
                                        value={btJob.data.pcapdirs[0]}
                                        onChange={handleUpdatePCAPDirectory}
                                    ></TextField> */}
                                </Box>
                                <Box width="10%">
                                    <Box mt={1} ml={2}>
                                        <Tooltip title="Add directory to list">
                                            <IconButton color="primary" onClick={handleAddPCAPDir}>
                                                <AddIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Box>
                            {/* <Box mb={2}>
                                <TextField
                                    label="No. PCAP Files"
                                    fullWidth
                                ></TextField>
                            </Box> */}
                        </Grid>
                        {/* <Grid item xs={4}>
                            <Box mb={7.5}>
                                <Box><FormControlLabel label="Generate Metadata" control={<Checkbox checked={btJob.generateMetadata} onChange={handleGenerateMetadata} />} /></Box>
                                <Box><FormControlLabel label="Add Job to Queue" control={<Checkbox checked={btJob.addtoQueue} onChange={handleAddToQueue} />} /></Box>
                            </Box>
                            <Box>
                                <TextField
                                    fullWidth
                                    label="PCAP Processed"
                                />
                            </Box>
                            <Box mt={4}>
                                <Typography><b>Total Selection Count:</b> 5.7k</Typography>
                            </Box>
                        </Grid> */}
                        <Grid container item xs={12} justifyContent="flex-end">
                            <Box><Button variant="contained" color="secondary" onClick={handleCancel}>Cancel</Button></Box>
                            <Box ml={2}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCreateJob}
                                    disabled={!isValid()}
                                >
                                    {edit ? 'Update' : 'Create'} Job
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Box>
    );
};

export default BacktestingCreate;
